import React, {FunctionComponent, useCallback, useLayoutEffect, useMemo, useState} from "react";
import {ReduxActions, useAppDispatch, useAppSelector} from "../../redux";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Theme, useMediaQuery, useTheme} from "@mui/material";
import {FinalForm, Form, Mapbox, MapboxCoordinates, MapboxProps, MuiRFF, useForm, useIsMounted, Yup} from "../../../@bizkeytech";
import {DialogsReduxSliceStateUpsertRegisteredLocationEntryDataLocation} from "../../redux/slices/dialogs";
import {MapboxCircle} from "../../../@bizkeytech/mapbox/ui/circles";
import Field from "../../components/field";
import Texts from "../../../core/models/texts";

const startingRadius = 100;

interface Data extends DialogsReduxSliceStateUpsertRegisteredLocationEntryDataLocation {
    starting?: boolean;
}


const schema = Yup.object({
    name: Yup.string().trim().required(Texts.FormValidation.requiredField),
    radius: Yup.number().required(Texts.FormValidation.requiredField).minNumber(0, Texts.FormValidation.minNumber(0)),
    latitude: Yup.number().required(Texts.FormValidation.requiredField),
    longitude: Yup.number().required(Texts.FormValidation.requiredField),
    address: Yup.string().trim().optional(),
    starting: Yup.boolean().optional(),
});

interface Schema extends Yup.InferType<typeof schema> {
}

type SchemaKeys = keyof Schema;

const UpsertRegisteredLocationDialog: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const state = useAppSelector(state => state.dialogs?.upsertRegisteredLocation);
    const [location, setLocation] = useState<Omit<Data, 'id'>>();
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const formId = useMemo(() => 'upsert-registered-location-form', []);
    const isMobile = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));

    const {hideCloseButton, disableBackdropClick, ...dialogProps} = state;

    /**
     * Updates the marker state with the coordinates of the selected address.
     * @param location
     */
    const getStartingLocation = useCallback(async (location?: DialogsReduxSliceStateUpsertRegisteredLocationEntryDataLocation) => {
        if (location) {
            setLocation({
                ...location,
                starting: true,
            });
        } else {
            const position = await new Promise<GeolocationPosition | null>(r => navigator.geolocation.getCurrentPosition(position => r(position), () => r(null)));
            if (!isMounted())
                return;
            if (position) {
                setLocation({
                    name: '',
                    address: '',
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    radiusInMeters: startingRadius,
                    starting: true,
                })
            }
        }
    }, [isMounted]);

    /**
     * As soon as the dialog opens, resets the state values.
     */
    useLayoutEffect(() => {
        if (state?.open) {
            setSubmitting(false);
            setLocation(undefined);
            void getStartingLocation(state?.data?.location);
        }
    }, [getStartingLocation, state?.data?.location, state?.open])

    /**
     * Closes the dialog by dispatching its relevant action to the redux store.
     */
    const closeDialog = useCallback((successful = false) => {
        if (state?.onClose) {
            state.onClose(successful ? "successful" : "leave");
        }
        dispatch(ReduxActions.dialogs.upsertRegisteredLocation({open: false}));
    }, [state, dispatch])

    /**
     * Closes the dialog if the values from the state do not prevent the closing of the dialog.
     */
    const onDialogClosed = useCallback((event: Object, reason: 'backdropClick' | 'escapeKeyDown' | 'closeClick') => {
        switch (reason) {
            case "backdropClick":
                if (!state.disableBackdropClick && !submitting)
                    closeDialog();
                break;
            case "closeClick":
                if (!state.hideCloseButton && !submitting)
                    closeDialog();
                break;
            case "escapeKeyDown":
                if (!state.disableEscapeKeyDown && !submitting)
                    closeDialog();
                break;
            default:
                break;
        }
    }, [submitting, closeDialog, state.disableBackdropClick, state.disableEscapeKeyDown, state.hideCloseButton])

    /**
     * Submits the location to the server with the provided values.
     */
    const submitLocation = useCallback<FinalForm.Config<Schema>["onSubmit"]>(async (values) => {
        const location = {
            name: values.name,
            address: values.address,
            latitude: values.latitude,
            longitude: values.longitude,
            radiusInMeters: values.radius,
        }
        setSubmitting(true);
        let resultFlag = false;
        if (!!state.data?.location && state.data?.update) {
            resultFlag = await state.data.update({
                ...location,
                id: state.data.location.id,
            });
        } else if (!!state.data?.create) {
            resultFlag = await state.data.create(location);
        }
        setSubmitting(false);
        if (resultFlag)
            closeDialog(true);
    }, [closeDialog, state.data])


    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), []);

    const initialValues = useMemo<Partial<Schema>>(() => ({
        name: location?.name ?? '',
        radius: location?.radiusInMeters ?? startingRadius,
        latitude: location?.latitude,
        longitude: location?.longitude,
        address: location?.address,
        starting: location?.starting,
    }), [location?.address, location?.latitude, location?.longitude, location?.name, location?.radiusInMeters, location?.starting]);

    return (
        <>
            <Dialog
                {...dialogProps}
                fullScreen={isMobile}
                onClose={onDialogClosed}
            >
                <DialogTitle fontWeight={600}>
                    {
                        !!state.data?.location ? "Edit Registered Location" : "Register New Location"
                    }
                </DialogTitle>
                <DialogContent>
                    <Form<Schema, Partial<Schema>>
                        onSubmit={submitLocation}
                        validate={validate}
                        initialValues={initialValues}
                        formProps={{id: formId}}
                    >
                        <UpsertRegisteredLocationDialogForm/>
                    </Form>
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'flex-end'}}>
                    <Button
                        variant={'text'}
                        size={'medium'}
                        type={'button'}
                        color={'primary'}
                        disabled={submitting}
                        onClick={() => onDialogClosed({}, 'closeClick')}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant={'contained'}
                        size={'medium'}
                        type={'submit'}
                        form={formId}
                        color={'primary'}
                        disabled={submitting}
                    >
                        {
                            submitting
                                ? "Submitting..."
                                : "Submit"
                        }
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}


const UpsertRegisteredLocationDialogForm: FunctionComponent = () => {
    const {form, values: location} = useForm<Schema, Partial<Schema>>() ?? {};

    const required = useMemo<Record<SchemaKeys, boolean>>(() => MuiRFF.makeRequired(schema), []);

    const theme = useTheme();
    const marker = useMemo<MapboxCoordinates | undefined>(() => !!location?.latitude && !!location?.longitude ? {lat: location.latitude, lng: location.longitude} : undefined, [location]);
    const circles = useMemo<MapboxCircle[]>(() => !!marker ? [{
        id: 'marker-location-circle',
        center: marker,
        radiusInMeters: location?.radius ?? startingRadius,
        fillColor: location?.starting ? theme.palette.success.main : theme.palette.primary.main,
        fillOutlineColor: location?.starting ? theme.palette.success.main : theme.palette.primary.main,
        fillOpacity: 0.2,
        markerIcon: true,
        markerColor: location?.starting ? theme.palette.success.main : theme.palette.primary.main,
    }] : [], [location?.radius, location?.starting, marker, theme.palette.primary.main, theme.palette.success.main]);

    const startingCoordinates = useMemo<MapboxCoordinates | undefined>(() => {
        if (location) return {lat: location.latitude, lng: location.longitude}
        return undefined;
    }, [location]);

    /**
     * Updates the location address with the provided mapbox address.
     * @param address  The address selected from the mapbox search.
     */
    const onAddressSelect = useCallback<Required<MapboxProps>['onAddressSelect']>((address) => {
        form?.change('starting', undefined);
        form?.change('address', [address.address, address.city, address.province, address.country, address.postalCode].filter(Boolean).join(', '));
    }, [form])

    /**
     * Updates the location coordinates with the provided mapbox coordinates.
     * @param coordinates  The coordinates selected from the mapbox search.
     */
    const onCoordinatesSelect = useCallback<Required<MapboxProps>['onCoordinatesSelect']>((coordinates) => {
        form?.change('starting', undefined);
        form?.change('latitude', coordinates.lat);
        form?.change('longitude', coordinates.lng);
    }, [form])

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                <Mapbox
                    sx={{width: '100%', minHeight: 400, height: 400}}
                    circles={circles}
                    startingCoordinates={startingCoordinates}
                    onAddressSelect={onAddressSelect}
                    onCoordinatesSelect={onCoordinatesSelect}
                    flyToMarkerOnClick={!location?.starting}
                />
                <Field.TextField<'text', true>
                    readonly
                    label={'Address'}
                    value={location?.address}
                    autoCommit={false}
                />
                <MuiRFF.TextField
                    label={'Name'}
                    name={'name'}
                    required={required.name}
                    fullWidth
                />
                <MuiRFF.TextField
                    label={'Radius'}
                    name={'radius'}
                    required={required.radius}
                    type={'number'}
                    fullWidth
                />
            </Box>
        </>
    );
}

export default UpsertRegisteredLocationDialog;
